import React, { useCallback } from 'react';
import { Box, Container, Grid } from '@mui/material';
import Particles from "react-tsparticles";
import { loadSlim } from "tsparticles-slim";
import { alpha } from '@mui/material/styles';
import BrandSection from './Footer/BrandSection';
import QuickLinks from './Footer/QuickLinks';
import WasteCategories from './Footer/WasteCategories';
import ContactInfo from './Footer/ContactInfo';
import FooterBottom from './Footer/FooterBottom';

const Footer = () => {
    // Particles configuration for footer background with theme.js colors
    const particlesInit = useCallback(async (engine) => {
        await loadSlim(engine);
    }, []);

    const particlesConfig = {
        background: {
            color: {
                value: "transparent",
            },
        },
        fpsLimit: 60,
        interactivity: {
            events: {
                onHover: {
                    enable: true,
                    mode: "repulse",
                },
                resize: true,
            },
            modes: {
                repulse: {
                    distance: 80,
                    duration: 0.4,
                },
            },
        },
        particles: {
            color: {
                value: ["#43a047", "#66bb6a", "#2e7d32", "#4caf50", "#81c784"],
            },
            links: {
                enable: false,
            },
            move: {
                direction: "none",
                enable: true,
                outModes: {
                    default: "bounce",
                },
                random: true,
                speed: 0.8,
                straight: false,
            },
            number: {
                density: {
                    enable: true,
                    area: 1500,
                },
                value: 12,
            },
            opacity: {
                value: 0.2,
                random: true,
                animation: {
                    enable: true,
                    speed: 1,
                    minimumValue: 0.1,
                    sync: false,
                },
            },
            shape: {
                type: ["circle", "triangle", "polygon"],
                options: {
                    polygon: {
                        sides: 6,
                    },
                },
            },
            size: {
                value: { min: 4, max: 16 },
                random: true,
                animation: {
                    enable: true,
                    speed: 1.5,
                    minimumValue: 2,
                    sync: false,
                },
            },
        },
        detectRetina: true,
    };

    return (
        <Box sx={{ mt: 'auto' }}>
            {/* Main Footer Content with Particles and Modern Design */}
            <Box sx={{
                background: 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 25%, #388e3c 50%, #43a047 100%)',
                color: 'white',
                pt: 8,
                pb: 3,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `radial-gradient(circle at 20% 50%, ${alpha('#66bb6a', 0.15)} 0%, transparent 50%),
                                 radial-gradient(circle at 80% 20%, ${alpha('#81c784', 0.12)} 0%, transparent 50%),
                                 radial-gradient(circle at 40% 80%, ${alpha('#4caf50', 0.1)} 0%, transparent 50%)`,
                    zIndex: 1,
                    pointerEvents: 'none'
                },
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, #ffc400 0%, #66bb6a 25%, #43a047 50%, #2e7d32 75%, #1b5e20 100%)',
                    zIndex: 3
                }
            }}>
                {/* Enhanced Particles Background */}
                <Particles
                    id="footer-particles"
                    init={particlesInit}
                    options={particlesConfig}
                    style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        zIndex: 0,
                    }}
                />

                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
                    <Grid container spacing={4}>
                        {/* Brand Section */}
                        <Grid item xs={12} sm={6} md={4}>
                            <BrandSection />
                        </Grid>

                        {/* Quick Links */}
                        <Grid item xs={6} sm={3} md={2}>
                            <QuickLinks />
                        </Grid>

                        {/* Waste Categories */}
                        <Grid item xs={6} sm={3} md={2}>
                            <WasteCategories />
                        </Grid>

                        {/* Contact & Info */}
                        <Grid item xs={12} sm={6} md={4}>
                            <ContactInfo />
                        </Grid>
                    </Grid>

                    {/* Footer Bottom */}
                    <FooterBottom />
                </Container>
            </Box>
        </Box>
    );
};

export default Footer;
